import { useEffect, useState } from 'react'
import { Button, Spin } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { getStockQuarterlyFinancialDetail } from '@/api/stock'
import type { StockFinancialData } from '@/types/stockPool'
import { reportTypeLabel, buildFields, StepBlock } from '../report'
import '../report/index.scss'

// ── 主组件 ──────────────────────────────────────────────────────────────────
export default function QuarterlyReportDetailPage() {
    const navigate = useNavigate()
    const { code, reportId } = useParams<{ code: string; reportId: string }>()
    const [ind, setInd] = useState<StockFinancialData | null>(null)
    const [loading, setLoading] = useState(true)

    const decodedCode = decodeURIComponent(code ?? '')

    useEffect(() => {
        if (!code || !reportId) return
        setLoading(true)
        getStockQuarterlyFinancialDetail(Number(reportId))
            .then(res => {
                setInd(res.item)
            })
            .catch(() => {
                setInd(null)
            })
            .finally(() => setLoading(false))
    }, [code, reportId])

    if (loading) return (
        <div className="rpt" style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Spin size="large" />
        </div>
    )

    if (!ind) return (
        <div className="rpt">
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)' }}>
                季度财报记录不存在
                <br />
                <Button style={{ marginTop: 16 }} onClick={() => navigate(`/stock-kb/${code}?tab=5`)}>返回</Button>
            </div>
        </div>
    )

    const cur = ind.currency ?? 'HKD'
    const anyInd = ind as any
    const rtLabel = reportTypeLabel[anyInd.report_type ?? ''] ?? anyInd.report_type ?? '—'
    const fields = buildFields(cur)

    return (
        <div className="rpt">
            {/* 顶部标题栏 */}
            <div className="page-header flex flex-between rpt__header">
                <div className="rpt__title-group">
                    <Button size="small" type="text" className="rpt__back" icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(`/stock-kb/${encodeURIComponent(decodedCode)}?tab=5`)}>
                        {decodedCode}
                    </Button>
                    /
                    <span className="rpt__period">{anyInd.fiscal_year ?? '—'}</span>
                    <span className="rpt__type-tag">{rtLabel}</span>
                    <span className="rpt__date-hint">{ind.report_date}</span>
                </div>
                <div className="rpt__header-right">
                    <span className="rpt__price-label">报告期收盘</span>
                    <span className="rpt__price">
                        {cur === 'HKD' ? 'HK$' : cur === 'USD' ? '$' : '¥'}{ind.closing_price?.toFixed(2) ?? '—'}
                    </span>
                </div>
            </div>

            {/* 字段列表 */}
            <div className="rpt__content">
                <table className="rpt__table">
                    <tbody>
                        {fields.map((f, i) => (
                            <>
                                {f.section && (
                                    <tr key={`sec-${i}`} className="rpt__section-row">
                                        <td colSpan={2}>{f.section}</td>
                                    </tr>
                                )}
                                <tr key={f.label} className="rpt__row">
                                    <td className="rpt__row-label">{f.label}</td>
                                    <td className="rpt__row-value">{f.render(ind)}</td>
                                </tr>
                            </>
                        ))}
                    </tbody>
                </table>
                {anyInd.indicators_step_1_md && (
                    <StepBlock title="AI 提取数据（Step 1）" content={anyInd.indicators_step_1_md} />
                )}
                {anyInd.indicators_step_2_md && (
                    <StepBlock title="AI 提取数据（Step 2）" content={anyInd.indicators_step_2_md} />
                )}
            </div>
        </div>
    )
}
